import React, { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import {
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material'
import { PageWrapper } from 'components/Modules/Wrappers/PageWrapper'
import { FormInput } from 'components/Elements/Inputs/FormInput'
import { Button } from 'components/Elements/Buttons/Button'
import { Toast } from 'components/Elements/Feedback/Toast'
import { useOptOutInfo } from 'api/useOptOutInfo'
import { useOptOutSubmit } from 'api/useOptOutSubmit'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface OptOutProps {}

interface OptOutFormValues {
  email: string
}

export const OptOut: React.FC<OptOutProps> = () => {
  const [searchParams] = useSearchParams()
  const physicianId = searchParams.get('physicianId') ?? undefined

  const { optOutInfo, isLoading, isError } = useOptOutInfo({ physicianId })
  const [confirmed, setConfirmed] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<OptOutFormValues>({ mode: 'onChange', defaultValues: { email: '' } })

  useEffect(() => {
    if (optOutInfo?.email) {
      reset({ email: optOutInfo.email })
    }
  }, [optOutInfo?.email, reset])

  const { submitOptOutAsync, isLoading: isSubmitting, isSuccess } = useOptOutSubmit({
    onError: () => setToastMessage('Something went wrong submitting your opt-out. Please try again.'),
  })

  const onSubmit = async ({ email }: OptOutFormValues) => {
    if (!physicianId || !optOutInfo) return
    await submitOptOutAsync({ physicianId, email })
  }

  if (!physicianId) {
    return (
      <PageWrapper>
        <Typography variant="h5" component="h1">
          Invalid opt-out link
        </Typography>
        <Typography mt={1}>
          This link is missing required information. Please contact NICA if you believe this is an error.
        </Typography>
      </PageWrapper>
    )
  }

  if (isLoading) {
    return (
      <PageWrapper>
        <Stack alignItems="center" mt={5}>
          <CircularProgress />
        </Stack>
      </PageWrapper>
    )
  }

  if (isError || !optOutInfo) {
    return (
      <PageWrapper>
        <Typography variant="h5" component="h1">
          We couldn't load this opt-out link
        </Typography>
        <Typography mt={1}>
          Please try again later or contact NICA for assistance.
        </Typography>
      </PageWrapper>
    )
  }

  if (isSuccess) {
    return (
      <PageWrapper>
        <Typography variant="h5" component="h1">
          You've been opted out
        </Typography>
        <Typography mt={1}>
          {optOutInfo.physicianName} has been opted out of participating.
        </Typography>
      </PageWrapper>
    )
  }

  return (
    <PageWrapper sx={{ paddingX: 0 }}>
      <Stack spacing={2} p={3} alignItems="center">
        <Card sx={{ width: '100%', maxWidth: 600 }}>
          <CardContent>
            <Typography variant="h5" component="h1" mb={2}>
              Are you {optOutInfo.physicianName} opting out of participating?
            </Typography>
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <Stack spacing={2}>
                <FormInput
                  control={control}
                  name="email"
                  label="Email"
                  color="primary"
                  variant="outlined"
                  size="medium"
                  fullWidth
                  rules={{
                    required: 'Email is required',
                    pattern: { value: EMAIL_PATTERN, message: 'Enter a valid email address' },
                  }}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmed}
                      onChange={(e) => setConfirmed(e.target.checked)}
                    />
                  }
                  label="I confirm I am opting out of participation."
                />
                <Button
                  type="submit"
                  size="medium"
                  variant="contained"
                  color="primary"
                  disabled={!confirmed || !isValid || isSubmitting}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Submit
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Stack>
      <Toast
        open={!!toastMessage}
        message={toastMessage}
        severity="error"
        onClose={() => setToastMessage('')}
      />
    </PageWrapper>
  )
}

export default OptOut
